import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Website from './pages/Website';
import PrivacyPolicy from './pages/Privacy';
import Articles from './pages/Articles';
import Article from './pages/Article';
import { TranslationProvider } from './context/TranslationContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { CookieConsentBanner } from './components/CookieConsent';
import { usePageTracking } from './hooks/usePageTracking';

/**
 * Language comes from the URL prefix, falling back to the hostname: the
 * "avocat*" domains default to Romanian, kovacstimeaugyved.ro to Hungarian.
 *
 * Derived from the router's location rather than window.location so that a
 * language switch is a client-side navigation. Reading window.location once
 * at mount meant every switch had to reload the page, which cold-loaded the
 * prerendered HTML and flashed it before React re-rendered.
 */
function useLanguageFromRoute(): 'ro' | 'hu' | 'en' {
  const { pathname } = useLocation();
  if (pathname.startsWith('/ro')) return 'ro';
  if (pathname.startsWith('/en')) return 'en';
  if (pathname.startsWith('/hu')) return 'hu';
  return window.location.hostname.includes('avocat') ? 'ro' : 'hu';
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <LocalisedApp />
      </Router>
    </ErrorBoundary>
  );
}

function LocalisedApp() {
  const lang = useLanguageFromRoute();

  return (
    <TranslationProvider lang={lang}>
      <AppContent />
    </TranslationProvider>
  );
}

function AppContent() {
  usePageTracking(); // Automatically tracks page views

  return (
    <div className="App">
      <Routes>
        <Route path="/" element={<Website />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/hu/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/en/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/ro/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/articles" element={<Articles />} />
        <Route path="/hu/articles" element={<Articles />} />
        <Route path="/en/articles" element={<Articles />} />
        <Route path="/ro/articles" element={<Articles />} />
        <Route path="/articles/:slug" element={<Article />} />
        <Route path="/hu/articles/:slug" element={<Article />} />
        <Route path="/en/articles/:slug" element={<Article />} />
        <Route path="/ro/articles/:slug" element={<Article />} />
        <Route path="/hu" element={<Website />} />
        <Route path="/en" element={<Website />} />
        <Route path="/ro" element={<Website />} />
      </Routes>
      <CookieConsentBanner />
    </div>
  );
}

export default App;